import type { FlowResult } from '../../../../../shared/player-contracts/src'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { DeckTemplateSettings } from '../../deck/templateSettings'
import { useI18n } from '../../i18n'
import {
  fetchDeckPlayerBootstrap,
  postDeckPlayerReset,
  postDeckPlayerRetryRequested,
} from '../api/deckPlayerApi'
import type { DeckPlayerBootstrapV1, DeckPlayerModel } from '../types'
import {
  allowReplayWhenCompleted as replayAllowedBy,
  customSavepointConfig,
  initialCheckboxValues,
  initialFlowPhaseId as resumePhaseOf,
  retryCooldown as cooldownOf,
  toDeckCards,
} from './bootstrapMapping'
import {
  clearFlowEndAt,
  isToday,
  mergeDeckTemplate,
  midnightTonight,
  readFlowEndAt,
  readLocalDeckTemplate,
  readLocalRetryRequested,
  templateStorageKeys,
  writeFlowEndAt,
  writeLocalRetryRequested,
} from './hostStorage'
import { coercePlayerProps, isDebugEnabled, withUserRewards } from './playerProps'
import { toErrorString, useDeckErrorDiagnostics } from './useDeckErrorDiagnostics'

type Props = {
  deckId: string
  model: DeckPlayerModel
  passthroughProps?: Record<string, unknown>
  restRoot?: string
  nonce?: string
  /** Logged with the bootstrap: the graph the editor preview plays instead. */
  overrideFlowGraph?: unknown
  onPhaseChange?: (phaseId: string) => void
  onComplete?: (result: FlowResult) => void
  onLoad?: () => void
  onShowMedia?: (info: {
    mediaType: 'image' | 'video' | 'audio' | 'pdf'
    mediaName: string
    mediaId: string
  }) => void
}

const UNAUTHORIZED_CODES = ['rest_forbidden', 'rest_not_logged_in', 'rest_cannot_read']

/**
 * Loads a deck's player bootstrap and turns it into DeckPlayerShell props: cards,
 * saved progress, template settings, completion/retry state and resume point. Retry
 * and reset are handled here because they remount the shell.
 */
export function useDeckPlayerHost(props: Props) {
  const { t } = useI18n()

  const loading = ref(true)
  const error = ref<string | null>(null)
  const bootstrap = ref<DeckPlayerBootstrapV1 | null>(null)
  /** Changing the epoch remounts the shell (retry, continue, reset). */
  const shellEpoch = ref(0)
  const retryRequestedLocal = ref(false)
  /** Optimistic value right after clicking Retry, until the backend confirms. */
  const retryRequestedOverride = ref<boolean | null>(null)
  const flowEndAtTimestamp = ref<number | null>(null)

  const diagnostics = useDeckErrorDiagnostics(props, error)
  const debugEnabled = computed(() => isDebugEnabled(props.passthroughProps))

  const playerProps = computed(() =>
    withUserRewards(coercePlayerProps(props.passthroughProps), bootstrap.value?.user),
  )

  // Deck template (backend + unsaved local editor settings)
  /** The bootstrap deck id matches the key the deck editor writes. */
  const deckIdForTemplate = computed(() => String(bootstrap.value?.deck?.id || props.deckId))
  const localDeckTemplate = ref<DeckTemplateSettings>({})
  const refreshLocalDeckTemplate = () => {
    localDeckTemplate.value = readLocalDeckTemplate(deckIdForTemplate.value, String(props.deckId))
  }
  const onStorage = (event: StorageEvent) => {
    if (event.storageArea !== window.localStorage) return
    const keys = templateStorageKeys(deckIdForTemplate.value, String(props.deckId))
    if (event.key && keys.includes(event.key)) refreshLocalDeckTemplate()
  }
  onMounted(() => {
    flowEndAtTimestamp.value = readFlowEndAt(props.deckId, props.model)
    refreshLocalDeckTemplate()
    window.addEventListener('storage', onStorage)
  })
  onBeforeUnmount(() => window.removeEventListener('storage', onStorage))
  watch(() => deckIdForTemplate.value, refreshLocalDeckTemplate)

  const deckTemplate = computed(() =>
    mergeDeckTemplate(bootstrap.value?.deck?.props, localDeckTemplate.value),
  )

  /** With deck propagation, a flow ended today blocks Continue until midnight. */
  const flowPropagationBlockedUntil = computed<string | null>(() => {
    if (!deckTemplate.value.deckPropagation) return null
    const ts = flowEndAtTimestamp.value
    if (ts === null || !isToday(ts)) return null
    return midnightTonight()
  })

  // Cards and progress
  const cards = computed(() => toDeckCards(bootstrap.value))
  const isLoggedIn = computed(() => bootstrap.value?.user?.isLoggedIn === true)
  const savepoint = computed(() => customSavepointConfig(props.passthroughProps))
  const suppressProgressWrites = computed(() => savepoint.value !== null)
  const initialValues = computed(() =>
    initialCheckboxValues(bootstrap.value, cards.value, savepoint.value),
  )
  const initialFlowPhaseId = computed(() => resumePhaseOf(bootstrap.value, savepoint.value))

  const backendRetryRequested = () => {
    const stats = bootstrap.value?.progress?.stats as { retryRequested?: unknown } | undefined
    return stats && typeof stats.retryRequested === 'boolean' ? stats.retryRequested : null
  }

  const retryRequested = computed(() => {
    if (typeof retryRequestedOverride.value === 'boolean') return retryRequestedOverride.value
    // The backend decides for logged-in users; others use the local fallback.
    if (isLoggedIn.value) {
      const backend = backendRetryRequested()
      if (backend !== null) return backend
    }
    return retryRequestedLocal.value
  })

  const deckIsCompleted = computed(
    () =>
      (bootstrap.value?.progress?.stats as { isCompleted?: unknown } | undefined)?.isCompleted ===
      true,
  )
  const forceComplete = computed(() => deckIsCompleted.value && !retryRequested.value)
  const allowReplayWhenCompleted = computed(() => replayAllowedBy(bootstrap.value))
  const retryCooldown = computed(() => cooldownOf(bootstrap.value, Date.now()))
  const retryAllowed = computed(() => allowReplayWhenCompleted.value && retryCooldown.value.allowed)

  // Keep the local fallback in sync with the backend.
  watch(backendRetryRequested, (value) => {
    if (typeof value !== 'boolean') return
    retryRequestedLocal.value = value
    writeLocalRetryRequested(props.deckId, value)
  })

  const shellKey = computed(
    () => `${props.deckId}:${retryRequested.value ? 'retry' : 'normal'}:${shellEpoch.value}`,
  )

  /** Applies a retry flag at once, then persists it for logged-in users (best effort). */
  const setRetryRequested = async (value: boolean) => {
    retryRequestedOverride.value = value
    retryRequestedLocal.value = value
    writeLocalRetryRequested(props.deckId, value)
    if (!isLoggedIn.value) return
    try {
      await postDeckPlayerRetryRequested({
        deckId: props.deckId,
        model: props.model,
        retryRequested: value,
        restRoot: props.restRoot,
        nonce: props.nonce,
      })
      if (bootstrap.value?.progress?.stats) {
        bootstrap.value = {
          ...bootstrap.value,
          progress: {
            ...bootstrap.value.progress,
            stats: { ...bootstrap.value.progress.stats, retryRequested: value },
          },
        }
      }
      retryRequestedOverride.value = null // the backend is the source of truth again
    } catch (e) {
      if (import.meta.env.DEV) console.warn('Deckplayer: failed to persist retryRequested flag', e)
    }
  }

  const resetBackendProgress = () =>
    postDeckPlayerReset({
      deckId: props.deckId,
      model: props.model,
      restRoot: props.restRoot,
      nonce: props.nonce,
    })

  /** Starts a replay of a completed deck with cleared checkboxes. */
  const handleRetry = async () => {
    if (!allowReplayWhenCompleted.value || !retryAllowed.value) return
    await setRetryRequested(true)
    // Clearing saved checkboxes keeps mid-run saving and reload restore working.
    if (isLoggedIn.value) {
      try {
        await resetBackendProgress()
        if (bootstrap.value?.progress) {
          bootstrap.value = {
            ...bootstrap.value,
            progress: { ...bootstrap.value.progress, cards: {} },
          }
        }
      } catch (e) {
        // Even if the reset fails, the retry still starts.
        if (import.meta.env.DEV) console.warn('Deckplayer: failed to reset progress for retry', e)
      }
    }
    shellEpoch.value += 1
  }

  const handleCompleted = async () => {
    if (retryRequested.value) await setRetryRequested(false)
  }

  const markFlowEnded = () => {
    const timestamp = Date.now()
    writeFlowEndAt(props.deckId, props.model, timestamp)
    flowEndAtTimestamp.value = timestamp
  }

  const handleFlowEnd = (payload: { deckId: string; result: FlowResult }) => {
    markFlowEnded()
    props.onComplete?.(payload.result)
  }

  const handlePhaseInterlude = (payload: { deckId: string; phaseId: string | null }) => {
    markFlowEnded()
    if (payload.phaseId) props.onPhaseChange?.(payload.phaseId)
  }

  const handleShowMedia = (payload: {
    mediaType: 'image' | 'video' | 'audio' | 'pdf'
    mediaName: string
    mediaId: string
  }) => props.onShowMedia?.(payload)

  const handleFlowContinue = () => {
    shellEpoch.value += 1
  }

  /** Forgets the flow end and all saved progress, then restarts. */
  const handleFlowReset = async () => {
    clearFlowEndAt(props.deckId, props.model)
    flowEndAtTimestamp.value = null
    if (isLoggedIn.value) {
      try {
        await resetBackendProgress()
      } catch {
        // Best effort.
      }
    }
    if (bootstrap.value?.progress) {
      bootstrap.value = {
        ...bootstrap.value,
        progress: { ...bootstrap.value.progress, cards: {}, flowProgress: undefined },
      }
    }
    shellEpoch.value += 1
  }

  const logBootstrap = () => {
    const overrideFlowGraph = props.overrideFlowGraph
    console.group('[PFC DeckPlayerHost] Bootstrap loaded')
    console.log('deckId:', props.deckId, '| model:', props.model)
    console.log('overrideFlowGraph prop:', overrideFlowGraph)
    console.log('bootstrap.deck.flowGraph:', bootstrap.value?.deck?.flowGraph ?? null)
    console.log('bootstrap.deck.cardIds:', bootstrap.value?.deck?.cardIds)
    console.log('bootstrap.deck (full):', JSON.parse(JSON.stringify(bootstrap.value?.deck ?? {})))
    console.log('bootstrap (full):', JSON.parse(JSON.stringify(bootstrap.value ?? {})))
    console.log(
      'RESOLVED flow-graph used by player:',
      overrideFlowGraph !== undefined
        ? overrideFlowGraph
        : (bootstrap.value?.deck?.flowGraph ?? null),
    )
    console.groupEnd()
  }

  const load = async () => {
    loading.value = true
    error.value = null
    diagnostics.reset()
    if (props.model !== 'checkbox' && props.model !== 'flowchart') {
      bootstrap.value = null
      error.value = t('deckPlayer.shell.unsupportedModel')
      loading.value = false
      return
    }
    try {
      bootstrap.value = await fetchDeckPlayerBootstrap({
        deckId: props.deckId,
        model: props.model,
        includeCards: true,
        restRoot: props.restRoot,
        nonce: props.nonce,
      })
      logBootstrap()
    } catch (err) {
      const apiError = diagnostics.record(err)
      error.value =
        apiError?.code && UNAUTHORIZED_CODES.includes(apiError.code)
          ? t('deckPlayer.shell.unauthorized')
          : (apiError?.message ?? toErrorString(err))
      bootstrap.value = null
    } finally {
      loading.value = false
    }
  }

  onMounted(() => {
    retryRequestedLocal.value = readLocalRetryRequested(props.deckId)
    void load()
  })
  watch(loading, (isLoading) => {
    if (!isLoading) void nextTick(() => props.onLoad?.())
  })
  watch(
    () => props.deckId,
    (deckId) => {
      retryRequestedLocal.value = readLocalRetryRequested(deckId)
    },
  )
  watch(
    () => [props.deckId, props.model] as const,
    () => void load(),
  )

  return {
    t,
    loading,
    error,
    bootstrap,
    diagnostics,
    debugEnabled,
    playerProps,
    deckTemplate,
    flowPropagationBlockedUntil,
    cards,
    isLoggedIn,
    suppressProgressWrites,
    initialValues,
    initialFlowPhaseId,
    forceComplete,
    allowReplayWhenCompleted,
    retryAllowed,
    retryCooldown,
    shellKey,
    handleRetry,
    handleCompleted,
    handleFlowEnd,
    handlePhaseInterlude,
    handleShowMedia,
    handleFlowContinue,
    handleFlowReset,
  }
}
